import * as Constants from '../constants/';

const initialState = {
  isLoading: false,
  isSaving: false,
  hasLoadedOnce: false,
  loadContext: {},
  saveContext: {},
  error: null,
  data: {
    archivedTypes: [],
    types: [],
    random: {},
    summary: {},
    distribution: {},
    distributionFilter: {},
    keys: [],
    values: {},
    search: {},
    browsedFilter: {}
  }
};

/**
 * Concatenates two lists of assets together, respecting UUID uniqueness
 * If there is a conflict, the asset in the first list will be preserved and the
 * asset in the second list will be discarded.
 *
 * @param original
 * @param additional
 */
function mergeAssetLists(original, additional) {
  const originalIds = original.map(asset => asset.uuid);
  const additionalIds = additional.map(asset => asset.uuid);
  const duplicateIds = originalIds.filter(id => additionalIds.includes(id));
  if (duplicateIds.length) {
    // How to handle the conflicts?
    // Do we want to keep the original? replace the original? keep the new?
    // For now, keeping the original and discarding the additional
    const nonConflicting = additional.filter(
      asset => !duplicateIds.includes(asset.uuid)
    );
    return [...original, ...nonConflicting];
  } else {
    return [...original, ...additional];
  }
}

/**
 * Updates a list of assets with a new asset. Behavior will be different depending on
 * edit vs. create vs. archive/unarchive
 *
 * When creating a new asset, the asset will be pre-pended to the list
 * When editing an asset, the matching asset in the list will be found and updated
 * (If no asset could be found a copy of the original list is returned)
 *
 * @param assets
 * @param action
 */
function updateAssetInList(assets, action) {
  if (action.mode === 'create') {
    return [action.data, ...assets];
  } else if (action.mode === 'archive' || action.mode === 'unarchive') {
    //remove the asset
    return assets.filter(a => a.uuid !== action.data.uuid);
  } else {
    //find the asset
    const index = assets.map(a => a.uuid).indexOf(action.data.uuid);
    if (index >= 0) {
      const arr = assets.slice(0);
      arr[index] = { ...action.data };
      return arr;
    } else {
      console.warn('Could not find asset to update');
      return [...assets];
    }
  }
}

function mergeTypes(types, archivedTypes, action) {
  if (action.mode === 'delete') {
    return {
      types: types.filter(type => type.uuid !== action.assetClass.uuid),
      archivedTypes: archivedTypes.filter(
        type => type.uuid !== action.assetClass.uuid
      )
    };
  }
  if (action.mode === 'create') {
    return {
      types: [...types, action.data],
      archivedTypes: [...archivedTypes]
    };
  } else {
    // need to find the asset class
    const unarchivedIndex = types.map(a => a.uuid).indexOf(action.data.uuid);
    const archivedIndex = archivedTypes
      .map(a => a.uuid)
      .indexOf(action.data.uuid);

    if (archivedIndex < 0 && unarchivedIndex < 0) {
      console.warn('Could not find asset class to update');
      return { types: [...types], archivedTypes: [...archivedTypes] };
    }

    // class was archived
    if (archivedIndex >= 0) {
      // class still is archived
      if (action.data.archived) {
        const arr = [...archivedTypes];
        arr[archivedIndex] = { ...action.data };
        return { types: [...types], archivedTypes: [...arr] };
      } else {
        // class is no longer archived, remove it from archived and add it to unarchived
        return {
          types: [...types, { ...action.data }],
          archivedTypes: archivedTypes.filter(
            type => type.uuid !== action.data.uuid
          )
        };
      }
      // class was not archived
    } else {
      // class still is unarchived
      if (!action.data.archived) {
        const arr = [...types];
        arr[unarchivedIndex] = { ...action.data };
        return { types: [...arr], archivedTypes: [...archivedTypes] };
      } else {
        // class is now archived, remove it from unarchived and add it to archived
        return {
          types: types.filter(type => type.uuid !== action.data.uuid),
          archivedTypes: [...archivedTypes, { ...action.data }]
        };
      }
    }
  }
}

export default function(state = initialState, action) {
  const loadContext = {};
  const saveContext = { ...state.saveContext };
  const context = action.load?.context || action.context;

  switch (action.type) {
    case Constants.REQUEST_ASSET_TYPES:
      return {
        ...state,
        isLoading: true,
        isLoadingAssetTypes: true,
        hasLoadedOnce: true
      };
    case Constants.UPDATE_ASSET_DISTRIBUTION_FILTER:
      return {
        ...state,
        data: {
          ...state.data,
          distributionFilter: action.selection
        }
      };
    case Constants.REQUEST_ASSETS:
      if (context) {
        loadContext[context] = true;
      }
      return {
        ...state,
        loadContext: {
          ...state.loadContext,
          ...loadContext
        }
      };
    case Constants.REQUEST_ASSET:
      if (context) {
        loadContext[context] = true;
      }
      return {
        ...state,
        loadContext: {
          ...state.loadContext,
          ...loadContext
        }
      };
    case Constants.RECEIVE_ASSET:
      if (context) {
        loadContext[context] = false;
      }

      if (action.error) {
        return {
          ...state,
          isLoading: false,
          loadContext: {
            ...state.loadContext,
            ...loadContext
          },
          error: action.error
        };
      }

      return {
        ...state,
        isLoading: false,
        loadContext: {
          ...state.loadContext,
          ...loadContext
        },
        data: {
          ...state.data,
          search: {
            ...state.data.search,
            [context]: {
              ...(state.data.search[context] || {}),
              [action.uuid]: { ...action.data }
            }
          }
        }
      };
    case Constants.RECEIVE_ASSETS:
      if (context) {
        loadContext[context] = false;
      }
      if (action.error) {
        return {
          ...state,
          isLoading: false,
          loadContext: {
            ...state.loadContext,
            ...loadContext
          },
          error: action.error
        };
      }

      const previousAssets = state.data.search[context]?.assets || [];
      const previousAssetPaging = state.data.search[context]?.paging;

      let updatedAssets = previousAssets;
      let updatedAssetPaging = previousAssetPaging;

      if (action.page?.page) {
        if (action.page.page > (previousAssetPaging?.page || 0)) {
          updatedAssets = mergeAssetLists(previousAssets, action.data.assets);
          updatedAssetPaging = action.data.paging;
        }
        if (action.load?.mode === 'reset') {
          updatedAssets = [...action.data.assets];
          updatedAssetPaging = action.data.paging;
        }
      } else {
        updatedAssets = [...action.data.assets];
      }

      return {
        ...state,
        isLoading: false,
        loadContext: {
          ...state.loadContext,
          ...loadContext
        },
        data: {
          ...state.data,
          search: {
            ...state.data.search,
            [context]: {
              paging: updatedAssetPaging,
              assets: updatedAssets
            }
          }
        }
      };
    case Constants.CLEAR_ASSET_SEARCH_CONTEXT:
      const search = {};
      if (state.data.search.browser) {
        search.browser = { ...state.data.search.browser };
      }
      return {
        ...state,
        data: {
          ...state.data,
          search
        }
      };
    case Constants.REQUEST_SAVE_ASSET:
      if (context) {
        saveContext[context] = { saveInitiated: true };
      }
      return {
        ...state,
        isSaving: true,
        saveContext: {
          ...state.saveContext,
          ...saveContext
        }
      };
    case Constants.REQUEST_SAVE_ASSET_CLASS:
      return { ...state, isSaving: true };
    case Constants.CLEAR_ASSET_SAVE_CONTEXT:
      if (!action.context) {
        return { ...state, saveContext: {} };
      }
      const clearContext = Array.isArray(action.context)
        ? action.context
        : [action.context];
      const updatedContext = {};
      Object.keys(state.saveContext).forEach(key => {
        if (!clearContext.includes(key)) {
          updatedContext[key] = state.saveContext[key];
        }
      });
      return { ...state, saveContext: updatedContext };

    case Constants.RECEIVE_SAVE_ASSET:
      if (context) {
        saveContext[context] = { saveCompleted: true };
      }
      if (action.error) {
        if (context) {
          saveContext[context] = { error: action.error };
        }
        return {
          ...state,
          isSaving: false,
          saveContext: {
            ...state.saveContext,
            ...saveContext
          },
          data: {
            ...state.data,
            lastSave: {
              error: action.error
            }
          }
        };
      }

      let updatedSearch = { ...state.data.search };
      if (action.context) {
        const updatedPaging = state.data.search[action.context]?.paging;
        if (updatedPaging) {
          switch (action.mode) {
            case 'create':
              updatedPaging.total += 1;
              break;
            case 'unarchive':
            case 'archive':
              updatedPaging.total -= 1;
              if (updatedPaging.total < 0) updatedPaging.total = 0;
              break;
            default:
              break;
          }
        }
        // children should not be merged, the parent will be refreshed in a callback which will include the new child
        if (!action.data.parent_id) {
          updatedSearch = {
            ...state.data.search,
            [action.context]: {
              paging: updatedPaging,
              assets: updateAssetInList(
                state.data.search[action.context]?.assets || [],
                action
              )
            }
          };
        } else {
          updatedSearch = {
            ...state.data.search,
            [action.context]: {
              paging: updatedPaging,
              assets: state.data.search[action.context]?.assets
            }
          };
        }
      }
      let lastSave = { ...state.data.lastSave };
      if (action.mode !== 'refresh') {
        lastSave = {
          mode: action.mode,
          asset: { ...action.data }
        };
      }

      return {
        ...state,
        isSaving: false,
        saveContext: {
          ...state.saveContext,
          ...saveContext
        },
        data: {
          ...state.data,
          search: updatedSearch,
          lastSave
        }
      };
    case Constants.RECEIVE_SAVE_ASSET_CLASS:
      if (action.error) {
        return {
          ...state,
          isSaving: false,
          data: {
            ...state.data,
            lastSave: {
              error: action.error
            }
          }
        };
      }
      return {
        ...state,
        isSaving: false,
        data: {
          ...state.data,
          ...mergeTypes(state.data.types, state.data.archivedTypes, action),
          lastSave: {
            mode: action.mode,
            assetClass: { ...action.data, ...action.assetClass }
          }
        }
      };
    case Constants.RECEIVE_ASSET_TYPES:
      if (action.error) {
        return {
          ...state,
          isLoading: false,
          isLoadingAssetTypes: false,
          error: action.error
        };
      }
      const archived = action.data.types.filter(type => type.archived);
      const types = action.data.types.filter(type => !type.archived);
      return {
        ...state,
        isLoading: false,
        isLoadingAssetTypes: false,
        data: {
          ...state.data,
          types,
          archivedTypes: archived
        }
      };
    case Constants.RECEIVE_RANDOM_ASSET:
      if (action.error) {
        return { ...state, isLoading: false, error: action.error };
      }
      return {
        ...state,
        isLoading: false,
        data: {
          ...state.data,
          random: action.data
        }
      };
    case Constants.RECEIVE_ASSET_SUMMARY:
      if (action.error) {
        return { ...state, isLoading: false, error: action.error };
      }
      return {
        ...state,
        isLoading: false,
        data: {
          ...state.data,
          summary: action.data.summary
        }
      };
    case Constants.REQUEST_ASSET_DISTRIBUTION:
      if (action?.loadContext) {
        loadContext[action.loadContext] = true;
      }
      return {
        ...state,
        loadContext: {
          ...state.loadContext,
          ...loadContext
        }
      };
    case Constants.RECEIVE_ASSET_DISTRIBUTION:
      if (action?.loadContext) {
        loadContext[action.loadContext] = false;
      }
      if (action.error) {
        return {
          ...state,
          loadContext: {
            ...state.loadContext,
            ...loadContext
          },
          isLoading: false,
          error: action.error
        };
      }

      // converts dist array [{ value: "type1", summary: { summary} }, ...] to an object { type1: { summary } }
      const distributionReducer = (mapped, row) => {
        const key = row.value ? row.value : '_null';
        mapped[key] = row.summary;
        return mapped;
      };
      const distribution = action.data.distribution.reduce(
        distributionReducer,
        {}
      );

      const previousPaging =
        state.data.distribution[action.key + '_paging'] || {};
      const previous = state.data.distribution[action.key] || {};

      let combined = previous;
      let paging = previousPaging;
      if (action.data?.paging?.page) {
        if (action.data.paging.page > (previousPaging.page || 0)) {
          combined = { ...previous, ...distribution };
          paging = action.data.paging;
        }
        if (action.loadContext.mode === 'reset') {
          combined = { ...distribution };
          paging = action.data.paging;
        }
      } else {
        combined = distribution;
      }

      return {
        ...state,
        loadContext: {
          ...state.loading,
          ...loadContext
        },
        isLoading: false,
        data: {
          ...state.data,
          distribution: {
            ...state.data.distribution,
            [action.key]: combined,
            [action.key + '_paging']: paging
          }
        }
      };
    case Constants.REQUEST_ASSET_ATTRIBUTE_VALUES:
      if (action?.loadContext) {
        loadContext[action.loadContext] = true;
      }
      return {
        ...state,
        loadContext: {
          ...state.loadContext,
          ...loadContext
        }
      };
    case Constants.RECEIVE_ASSET_ATTRIBUTE_VALUES:
      if (action?.loadContext) {
        loadContext[action.loadContext] = false;
      }
      if (action.error) {
        return {
          ...state,
          isLoading: false,
          loadContext: {
            ...state.loadContext,
            ...loadContext
          },
          error: action.error
        };
      }
      return {
        ...state,
        isLoading: false,
        loadContext: {
          ...state.loadContext,
          ...loadContext
        },
        data: {
          ...state.data,
          values: {
            ...state.data.values,
            [action.key]: action.data.values
          }
        }
      };
    case Constants.RECEIVE_ASSET_ATTRIBUTES:
      if (action.error) {
        return { ...state, isLoading: false, error: action.error };
      }
      return {
        ...state,
        isLoading: false,
        data: {
          ...state.data,
          keys: action.data.keys
        }
      };
    case Constants.UPDATE_ASSET_FILTER:
      if (action.error) {
        return { ...state, isLoading: false, error: action.error };
      }
      return {
        ...state,
        isLoading: false,
        data: {
          ...state.data,
          browsedFilter: {
            ...state.data.browsedFilter,
            ...action.filter
          }
        }
      };
    default:
      return state;
  }
}
